/**
 * 报告生成器
 * 
 * 核心功能：
 * 1. 生成复制报告（用于复制功能）
 * 2. 生成统计报告
 * 3. 生成详细的时间消费报告
 */

import { formatDateTime } from './TimeCalculator.js';

/**
 * 生成复制报告
 * 
 * @param {Object} restRecord - 调休记录
 * @param {Array} consumptionRecords - 相关的消费记录
 * @param {Array} overtimeRecords - 加班记录列表
 * @returns {string} 格式化的复制报告
 */
export function generateCopyReport(restRecord, consumptionRecords, overtimeRecords) {
  const lines = [];
  
  // 报告头部
  lines.push('=== 调休时间消费详情 ===');
  lines.push('');
  lines.push(`调休申请时间：${formatDateTime(restRecord.txbegintime)} - ${formatDateTime(restRecord.txendtime)}`);
  lines.push(`调休申请时长：${restRecord.duration} 小时`);
  lines.push('');
  
  // 按类型分组消费记录，过滤掉0小时的记录
  const overtimeConsumptions = consumptionRecords.filter(c => c.type === '调休' && c.deductionTime > 0);
  const annualLeaveConsumptions = consumptionRecords.filter(c => c.type === '年假' && c.deductionTime > 0);
  
  // 加班时间消费详情
  if (overtimeConsumptions.length > 0) {
    lines.push('--- 扣除对应加班时间 ---');
    
    let totalOvertimeHours = 0;
    
    overtimeConsumptions.forEach((consumption, index) => {
      const overtimeRecord = overtimeRecords.find(r => r.id === consumption.overtimeId);
      
      if (overtimeRecord) {
        lines.push(`${index + 1}. 加班时间段：${formatDateTime(overtimeRecord.startTime)} - ${formatDateTime(overtimeRecord.endTime)}`);
        lines.push(`   消费时间段：${formatDateTime(consumption.consumptionStartTime)} - ${formatDateTime(consumption.consumptionEndTime)}`);
        lines.push(`   消费时长：${consumption.deductionTime} 小时`);
        lines.push('');
        
        totalOvertimeHours += consumption.deductionTime;
      }
    });
    
    lines.push(`扣除对应加班时间共（${totalOvertimeHours} 小时）`);
    lines.push('');
  }
  
  // 年假时间消费详情
  if (annualLeaveConsumptions.length > 0) {
    lines.push('--- 扣除年假时间 ---');
    
    let totalAnnualLeaveHours = 0;
    
    annualLeaveConsumptions.forEach((consumption, index) => {
      lines.push(`${index + 1}. 年假消费：${consumption.deductionTime} 小时`);
      totalAnnualLeaveHours += consumption.deductionTime;
    });
    
    lines.push('');
    lines.push(`扣除年假时间共（${totalAnnualLeaveHours} 小时）`);
    lines.push('');
  }
  
  // 报告尾部
  const totalConsumedHours = consumptionRecords.reduce((sum, c) => sum + c.deductionTime, 0);
  lines.push('--- 消费汇总 ---');
  lines.push(`总消费时间：${totalConsumedHours} 小时`);
  lines.push(`生成时间：${formatDateTime(new Date())}`);
  
  return lines.join('\n');
}

/**
 * 生成统计报告
 * 
 * @param {Array} overtimeRecords - 加班记录列表
 * @param {Array} restRecords - 调休记录列表
 * @param {Array} consumptionRecords - 消费记录列表
 * @param {Object} annualLeave - 年假信息
 * @returns {Object} 统计报告对象
 */
export function generateStatisticsReport(overtimeRecords, restRecords, consumptionRecords, annualLeave) {
  // 计算加班统计
  const overtimeStats = calculateOvertimeStatistics(overtimeRecords, consumptionRecords);
  
  // 计算调休统计
  const restStats = calculateRestStatistics(restRecords, consumptionRecords);
  
  // 计算年假统计
  const annualLeaveStats = calculateAnnualLeaveStatistics(consumptionRecords, annualLeave);
  
  // 计算时间平衡
  const timeBalance = calculateTimeBalance(overtimeStats, restStats, annualLeaveStats);
  
  return {
    overtime: overtimeStats,
    rest: restStats,
    annualLeave: annualLeaveStats,
    balance: timeBalance,
    generatedAt: new Date().toISOString()
  };
}

/**
 * 计算加班统计
 */
function calculateOvertimeStatistics(overtimeRecords, consumptionRecords) {
  const totalHours = overtimeRecords.reduce((sum, record) => sum + record.duration, 0);
  
  const consumedHours = consumptionRecords
    .filter(c => c.type === '调休' && c.overtimeId)
    .reduce((sum, c) => sum + c.deductionTime, 0);
  
  const remainingHours = totalHours - consumedHours;
  
  return {
    totalRecords: overtimeRecords.length,
    totalHours: Math.round(totalHours * 100) / 100,
    consumedHours: Math.round(consumedHours * 100) / 100,
    remainingHours: Math.round(remainingHours * 100) / 100,
    utilizationRate: totalHours > 0 ? Math.round((consumedHours / totalHours) * 10000) / 100 : 0
  };
}

/**
 * 计算调休统计
 */
function calculateRestStatistics(restRecords, consumptionRecords) {
  const totalHours = restRecords.reduce((sum, record) => sum + record.duration, 0);
  
  const fromOvertimeHours = consumptionRecords
    .filter(c => c.type === '调休')
    .reduce((sum, c) => sum + c.deductionTime, 0);
  
  const fromAnnualLeaveHours = consumptionRecords
    .filter(c => c.type === '年假')
    .reduce((sum, c) => sum + c.deductionTime, 0);
  
  return {
    totalRecords: restRecords.length,
    totalHours: Math.round(totalHours * 100) / 100,
    fromOvertimeHours: Math.round(fromOvertimeHours * 100) / 100,
    fromAnnualLeaveHours: Math.round(fromAnnualLeaveHours * 100) / 100
  };
}

/**
 * 计算年假统计
 */
function calculateAnnualLeaveStatistics(consumptionRecords, annualLeave) {
  const usedHours = consumptionRecords
    .filter(c => c.type === '年假')
    .reduce((sum, c) => sum + c.deductionTime, 0);
  
  const totalHours = (annualLeave.totalDays || 0) * 8;
  const remainingHours = totalHours - usedHours;
  
  return {
    totalDays: annualLeave.totalDays || 0,
    totalHours: totalHours,
    usedHours: Math.round(usedHours * 100) / 100,
    remainingHours: Math.round(remainingHours * 100) / 100,
    remainingDays: Math.round((remainingHours / 8) * 100) / 100
  };
}

/**
 * 计算时间平衡
 */
function calculateTimeBalance(overtimeStats, restStats, annualLeaveStats) {
  const totalAvailableHours = overtimeStats.remainingHours + annualLeaveStats.remainingHours;
  
  return {
    totalAvailableHours: Math.round(totalAvailableHours * 100) / 100,
    overtimeBalance: overtimeStats.remainingHours,
    annualLeaveBalance: annualLeaveStats.remainingHours,
    workLifeBalance: {
      overtimeToRestRatio: overtimeStats.totalHours > 0 ? 
        Math.round((restStats.fromOvertimeHours / overtimeStats.totalHours) * 10000) / 100 : 0,
      description: '加班时间转化为调休的比例'
    }
  };
}

/**
 * 生成详细的时间消费报告
 * 
 * @param {Array} consumptionRecords - 消费记录列表
 * @param {Array} overtimeRecords - 加班记录列表
 * @param {Array} restRecords - 调休记录列表
 * @returns {Array} 详细报告数组
 */
export function generateDetailedConsumptionReport(consumptionRecords, overtimeRecords, restRecords) {
  return consumptionRecords.map(consumption => {
    const restRecord = restRecords.find(r => 
      r.txbegintime === consumption.restStartTime && 
      r.txendtime === consumption.restEndTime
    );
    
    let sourceInfo = {};
    
    if (consumption.type === '调休' && consumption.overtimeId) {
      const overtimeRecord = overtimeRecords.find(r => r.id === consumption.overtimeId);
      if (overtimeRecord) {
        sourceInfo = {
          type: '加班时间',
          sourceStartTime: overtimeRecord.startTime,
          sourceEndTime: overtimeRecord.endTime,
          sourceDuration: overtimeRecord.duration,
          consumptionStartTime: consumption.consumptionStartTime,
          consumptionEndTime: consumption.consumptionEndTime
        };
      }
    } else if (consumption.type === '年假') {
      sourceInfo = {
        type: '年假',
        sourceStartTime: consumption.restStartTime,
        sourceEndTime: consumption.restEndTime,
        sourceDuration: consumption.deductionTime,
        consumptionStartTime: consumption.consumptionStartTime,
        consumptionEndTime: consumption.consumptionEndTime
      };
    }
    
    return {
      id: consumption.id,
      restRecord: restRecord ? {
        id: restRecord.id,
        startTime: restRecord.txbegintime,
        endTime: restRecord.txendtime,
        duration: restRecord.duration
      } : null,
      consumption: {
        type: consumption.type,
        deductionTime: consumption.deductionTime,
        ...sourceInfo
      },
      formattedReport: generateCopyReport(
        restRecord, 
        [consumption], 
        consumption.overtimeId ? [overtimeRecords.find(r => r.id === consumption.overtimeId)] : []
      )
    };
  });
}

/**
 * 导出报告为文本格式
 * 
 * @param {Object} report - 报告对象
 * @param {string} format - 导出格式 ('txt' | 'csv' | 'json')
 * @returns {string} 格式化的报告文本
 */
export function exportReport(report, format = 'txt') {
  switch (format.toLowerCase()) {
    case 'csv':
      return exportToCSV(report);
    case 'json':
      return JSON.stringify(report, null, 2);
    case 'txt':
    default:
      return exportToText(report);
  }
}

/**
 * 导出为文本格式
 */
function exportToText(report) {
  const lines = [];
  
  lines.push('=== 牛马摆烂工具统计报告 ===');
  lines.push('');
  lines.push(`生成时间：${formatDateTime(report.generatedAt)}`);
  lines.push('');
  
  // 加班统计
  lines.push('--- 加班统计 ---');
  lines.push(`总记录数：${report.overtime.totalRecords}`);
  lines.push(`总加班时间：${report.overtime.totalHours} 小时`);
  lines.push(`已消费时间：${report.overtime.consumedHours} 小时`);
  lines.push(`剩余时间：${report.overtime.remainingHours} 小时`);
  lines.push(`利用率：${report.overtime.utilizationRate}%`);
  lines.push('');
  
  // 调休统计
  lines.push('--- 调休统计 ---');
  lines.push(`总记录数：${report.rest.totalRecords}`);
  lines.push(`总调休时间：${report.rest.totalHours} 小时`);
  lines.push(`来自加班时间：${report.rest.fromOvertimeHours} 小时`);
  lines.push(`来自年假时间：${report.rest.fromAnnualLeaveHours} 小时`);
  lines.push('');
  
  // 年假统计
  lines.push('--- 年假统计 ---');
  lines.push(`年假总天数：${report.annualLeave.totalDays} 天`);
  lines.push(`年假总时间：${report.annualLeave.totalHours} 小时`);
  lines.push(`已用时间：${report.annualLeave.usedHours} 小时`);
  lines.push(`剩余时间：${report.annualLeave.remainingHours} 小时`);
  lines.push(`剩余天数：${report.annualLeave.remainingDays} 天`);
  lines.push('');
  
  // 时间平衡
  lines.push('--- 时间平衡 ---');
  lines.push(`总可用时间：${report.balance.totalAvailableHours} 小时`);
  lines.push(`加班余额：${report.balance.overtimeBalance} 小时`);
  lines.push(`年假余额：${report.balance.annualLeaveBalance} 小时`);
  lines.push(`工作生活平衡：${report.balance.workLifeBalance.overtimeToRestRatio}%`);
  
  return lines.join('\n');
}

/**
 * 导出为CSV格式
 */
function exportToCSV(report) {
  const lines = [];
  
  lines.push('类型,项目,数值,单位');
  lines.push(`加班,总记录数,${report.overtime.totalRecords},条`);
  lines.push(`加班,总时间,${report.overtime.totalHours},小时`);
  lines.push(`加班,已消费,${report.overtime.consumedHours},小时`);
  lines.push(`加班,剩余,${report.overtime.remainingHours},小时`);
  lines.push(`调休,总记录数,${report.rest.totalRecords},条`);
  lines.push(`调休,总时间,${report.rest.totalHours},小时`);
  lines.push(`年假,总天数,${report.annualLeave.totalDays},天`);
  lines.push(`年假,已用,${report.annualLeave.usedHours},小时`);
  lines.push(`年假,剩余,${report.annualLeave.remainingHours},小时`);
  
  return lines.join('\n');
}
